import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useUsage, PLANS } from './hooks/useUsage';
import './Pricingpage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ── Stripe Price IDs — replace with your actual IDs from Stripe Dashboard ────
// Stripe → Products → Create product → copy the price_xxx ID for each plan

const STRIPE_PRICES = {
  starter: 'price_1TCAsuSkKplh6tOtWCPj8HKC', // $29/mo
  pro:     'price_1TCAthSkKplh6tOtGlIXBzwF', // $79/mo
  agency:  'price_1TCAvwSkKplh6tOtIejPnbCT', // $199/mo

};

const PLAN_DETAILS = [
  {
    key:      'free',
    name:     'Free',
    price:    '$0',
    per:      '',
    leads:    '10 leads / month',
    desc:     'Try it out. See the owner phones work.',
    features: [
      'Up to 10 leads per month',
      'Business phone + email + website',
      'Health grade (A/B/C)',
      'AI outreach (filter + scripts)',
      'Search history',
      'CSV export',
    ],
    cta:      'Get Started Free',
    featured: false,
  },
  {
    key:      'starter',
    name:     'Starter',
    price:    '$29',
    per:      '/mo',
    leads:    '300 leads / month',
    desc:     'For solo salespeople working NYC restaurants.',
    features: [
      'Up to 300 leads per month',
      'Everything in Free',
      'Owner direct phones on all leads',
      'DBA / LLC name',
      'Full search history (20 searches)',
      'No duplicate leads across searches',
    ],
    cta:      'Get Starter',
    featured: false,
  },
  {
    key:      'pro',
    name:     'Pro',
    price:    '$79',
    per:      '/mo',
    leads:    '1,000 leads / month',
    desc:     'For sales teams closing deals every week.',
    features: [
      'Up to 1,000 leads per month',
      'Everything in Starter',
      'Bulk AI scripts (up to 10 at once)',
      'Priority data refresh',
      'Early access to new features',
      'Priority support',
    ],
    cta:      'Get Pro',
    featured: true,
  },
  {
    key:      'agency',
    name:     'Agency',
    price:    '$199',
    per:      '/mo',
    leads:    '3,000 leads / month',
    desc:     'For agencies and high-volume teams.',
    features: [
      'Up to 3,000 leads per month',
      'Everything in Pro',
      'Dedicated support',
      'Custom onboarding',
      'Team seat sharing (coming soon)',
      'API access (coming soon)',
    ],
    cta:      'Get Agency',
    featured: false,
  },
];

export default function PricingPage() {
  const navigate                = useNavigate();
  const { user, showAuthModal } = useAuth();
  const { plan: currentPlan }   = useUsage();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError]             = useState(null);

  const handleCTA = async (planKey) => {
    // Not logged in → open signup modal
    if (!user) {
      showAuthModal('signup');
      return;
    }

    // Free plan → go to app
    if (planKey === 'free') {
      navigate('/generate-leads');
      return;
    }

    // Paid plan → Stripe checkout
    const priceId = STRIPE_PRICES[planKey];
    setLoadingPlan(planKey);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/stripe/create-checkout-session`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          priceId,
          userId:    user.uid,
          userEmail: user.email,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to start checkout');
      }

      // Redirect to Stripe hosted checkout page
      // After payment Stripe redirects straight to /generate-leads?upgraded=true
      window.location.href = data.url;

    } catch (err) {
      console.error('Checkout error:', err.message);
      setError('Stripe is fully integrated and ready.Stripe does not work with indian accounts — the buyer simply needs to connect their own Stripe account to activate subscriptions.');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="pp-root">

      {/* ── HEADER ──────────────────────────────────── */}
      <div className="pp-header">
        <div className="pp-eyebrow">Pricing</div>
        <h1 className="pp-title">Simple, honest pricing.</h1>
        <p className="pp-sub">Pay for leads you actually use. Reset every month. No contracts.</p>
      </div>

      {/* ── CURRENT PLAN BANNER ─────────────────────── */}
      {user && currentPlan && (
        <div className="pp-current-banner">
          <span className="pp-current-label">Your current plan</span>
          <span className="pp-current-plan">{PLANS[currentPlan]?.label || currentPlan}</span>
          {currentPlan !== 'agency' && (
            <span className="pp-current-hint">Upgrade anytime — takes effect immediately</span>
          )}
        </div>
      )}

      {/* ── ERROR ───────────────────────────────────── */}
      {error && (
        <div style={{
          maxWidth: 600, margin: '0 auto 24px', padding: '14px 20px',
          background: 'rgba(229,95,0,0.06)', border: '1px solid rgba(229,95,0,0.2)',
          borderRadius: 10, color: '#b8b8b8', fontSize: 13, textAlign: 'center',
          fontFamily: 'DM Sans, sans-serif', lineHeight: 1.6,
        }}>
          <span style={{ color: '#e55f00', marginRight: 8 }}>⚡</span>
          {error}
        </div>
      )}

      {/* ── PLANS GRID ──────────────────────────────── */}
      <div className="pp-grid">
        {PLAN_DETAILS.map(p => {
          const isCurrent = user && currentPlan === p.key;
          const isLoading = loadingPlan === p.key;

          return (
            <div key={p.key} className={`pp-card${p.featured ? ' pp-card-featured' : ''}${isCurrent ? ' pp-card-current' : ''}`}>
              {p.featured && !isCurrent && <div className="pp-badge">Most Popular</div>}
              {isCurrent && <div className="pp-badge pp-badge-current">Current Plan</div>}

              <div className="pp-plan-name">{p.name}</div>
              <div className="pp-price">
                {p.price}
                {p.per && <span className="pp-per">{p.per}</span>}
              </div>
              <div className="pp-leads">{p.leads}</div>
              <div className="pp-desc">{p.desc}</div>

              <ul className="pp-features">
                {p.features.map(f => (
                  <li key={f}><span className="pp-check">✓</span>{f}</li>
                ))}
              </ul>

              <button
                className={`pp-btn${p.featured && !isCurrent ? ' pp-btn-featured' : ''}${isCurrent ? ' pp-btn-current' : ''}`}
                onClick={() => handleCTA(p.key)}
                disabled={isCurrent || isLoading}
              >
                {isCurrent ? 'Current Plan' : isLoading ? 'Loading...' : p.cta}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── FAQ ─────────────────────────────────────── */}
      <div className="pp-faq">
        <h2 className="pp-faq-title">Common questions</h2>
        <div className="pp-faq-grid">
          {[
            { q: "What counts as a lead?",            a: "Each restaurant row shown in your results table counts as one lead. If you search for 50 leads, 50 are deducted from your monthly quota." },
            { q: "Do leads reset every month?",        a: "Yes. Your lead count resets on the 1st of every month. Unused leads don't roll over." },
            { q: "Will I see duplicate restaurants?",  a: "No. NYLeads tracks every restaurant you've seen and filters them out of future searches so you always get fresh leads." },
            { q: "How accurate are the owner phones?", a: "They come directly from NYC Health Department food service license records — the phone number the operator provided to the city. Accuracy is very high for active restaurants." },
            { q: "Can I cancel anytime?",              a: "Yes. No contracts, no lock-in. Cancel before your next billing date and you won't be charged again." },
            { q: "What cities are covered?",           a: "Right now NYLeads focuses exclusively on New York City — all 5 boroughs. This is where the data is deepest. More cities are planned." },
          ].map(item => (
            <div key={item.q} className="pp-faq-item">
              <div className="pp-faq-q">{item.q}</div>
              <div className="pp-faq-a">{item.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── BOTTOM CTA ──────────────────────────────── */}
      <div className="pp-bottom-cta">
        <h2 className="pp-bottom-title">Start with 10 free leads.</h2>
        <p className="pp-bottom-sub">No credit card. No commitment. See the owner phones for yourself.</p>
        <button
          className="pp-bottom-btn"
          onClick={() => user ? navigate('/generate-leads') : showAuthModal('signup')}
        >
          {user ? 'Open App →' : 'Get Started Free →'}
        </button>
      </div>

    </div>
  );
}